import React from 'react';
import { H3, LayoutSingleColumn, Page } from '../../components';
import SectionFooter from '../PageBuilder/SectionBuilder/SectionFooter';
import TopbarContainer from '../TopbarContainer/TopbarContainer';

import { useIntl } from '../../util/reactIntl';
import { useConfiguration } from '../../context/configurationContext';

import css from './AboutHouseSitMatchPage.module.css';

const AboutHouseSitMatchPage = props => {

  const config = useConfiguration();
  const intl = useIntl();

  const marketplaceName = config.marketplaceName;
  const schemaTitle = intl.formatMessage({ id: 'AboutHouseSitMatchPage.schemaTitle' }, { marketplaceName });
  const schemaDescription = intl.formatMessage({ id: 'AboutHouseSitMatchPage.schemaDescription' }, { marketplaceName });

  return (
    <Page
      title={schemaTitle}
      description={schemaDescription}
    >
      <LayoutSingleColumn
        topbar={<TopbarContainer />}
        footer={<SectionFooter />}
        mainColumnClassName={css.mainColumnClassName}
      >
        <div className={css.root}>
          <H3>
            HouseSit Match: The Global House and Pet Sitting Network
          </H3>
          <p>
            HouseSit Match connects homeowners with trusted house sitters and pet sitters, providing affordable solutions for travel and property care. Whether you're looking for reliable pet care, a secure home, or an authentic travel experience, we make it possible.
          </p>

          <p className={css.subtitle}>
            What Makes Us Stand Out?
          </p>

          <p className={css.info}>
            At HouseSit Match, we take pride in offering something truly unique in the world of house and pet sitting. What sets us apart? Here are just a few of the qualities that make us the preferred choice:
          </p>

          <ul className={css.list}>
            <li>
              <strong>Global Reach:</strong> With members in over 120 countries, we connect homeowners and sitters from all corners of the globe, allowing for a truly international network.
            </li>
            <li>
              <strong>Verified Sitters:</strong> Unlike many platforms, we have a rigorous verification process, ensuring that every sitter in our network is trustworthy and reliable.
            </li>
            <li>
              <strong>No Fees for Homeowners:</strong> Homeowners can list their properties and find the perfect sitter without having to pay a single penny in housesitter fees and are only subjected to membership costs, making it a cost-effective solution.
            </li>
            <li>
              <strong>Comprehensive Support:</strong> Our dedicated team offers personalised customer service to support both homeowners and sitters, helping with everything from booking to logistics.
            </li>
            <li>
              <strong>Safety and Security:</strong> We prioritise the safety of both homes and pets. Our platform includes clear communication tools, contracts, and legal templates to ensure peace of mind.
            </li>
            <li>
              <strong>Tailored for All Needs:</strong> Whether you're looking for short-term pet sitting or long-term house sitting, our platform accommodates every need, with flexible service levels to suit a variety of budgets.
            </li>
          </ul>

          <p className={css.info}>
            HouseSit Match isn't just another house sitting network—it's a community built on trust, security, and exceptional support for both homeowners and sitters.
          </p>

          <p className={css.subtitle}>
            Our Story: From Adventure to House Sitting
          </p>

          <p className={css.info}>
            HouseSit Match’s roots go beyond simply offering house sitting services. It all started with a personal story of adventure, resilience, and a desire to make travel affordable for everyone.
          </p>

          <p className={css.info}>
            Lamia Walker, our founder, began her journey in Sudan before being forced to move to London after a military coup. This dramatic life change sparked a passion for travel, and Lamia found ways to explore the world while minimising costs.
          </p>

          <p className={css.info}>
            After learning about the concept of house sitting, she realised it could change the way people travel and take care of their homes and pets. In 2013, HouseSit Match was born.
          </p>

          <p className={css.subtitle}>
            Why Choose HouseSit Match?
          </p>
          <p className={css.info}>
            When it comes to finding the right house sitting or pet sitting service, you want a platform that offers more than just basic listings. Here’s why HouseSit Match is the best choice for your needs:
          </p>

          <ul className={css.list}>
            <li>
              <strong>Trustworthy, Experienced Sitters:</strong> All of our sitters are carefully vetted, with many bringing years of experience in both house and pet care. You can rest assured your home and pets are in capable hands.
            </li>
            <li>
              <strong>Flexible Options for Homeowners:</strong> Whether you're looking for someone to watch over your house for a week or need long-term support while you're away, we offer flexible service packages to match your needs.
            </li>
            <li>
              <strong>Affordable Travel Opportunities:</strong> If you're a sitter, HouseSit Match gives you the chance to travel the world and save on accommodation costs. Live like a local, experience new cultures, and indulge your love for animals—all for free.
            </li>
            <li>
              <strong>Access to Resources:</strong> As a member, you gain access to our exclusive house-sitting handbook, travel podcast, and other helpful resources that guide you through every step of the process.
            </li>
            <li>
              <strong>Easy, Secure Communication:</strong> Our platform offers tools that make it simple to communicate with homeowners and sitters, providing seamless coordination and transparency.
            </li>
            <li>
              <strong>A Community of Like-minded Individuals:</strong> HouseSit Match is more than just a platform—it's a global community of passionate homeowners and sitters working together to make travel and pet care more accessible and affordable.
            </li>
          </ul>

          <p className={css.subtitleSmall}>
            Want to Learn More?
          </p>
          <p className={css.info}>
            For a deeper dive into the benefits for homeowners and house sitters, visit our detailed resources:
          </p>

          <ul className={css.list}>
            <li>
              Benefits for Homeowners
            </li>
            <li>
              Benefits for House Sitters
            </li>
          </ul>

          <p className={css.subtitle}>
            HouseSit Match: Your Trusted Partner
          </p>

          <p className={css.info}>
            We’re not just another house sitting website. HouseSit Match is a community-driven platform designed to connect homeowners with the best house and pet sitters around the world.
            Whether you're a homeowner seeking peace of mind or a traveller looking for your next adventure, HouseSit Match offers a trusted, secure, and affordable way to meet your needs.
          </p>
          <p className={css.subtitle}>
            Join Our House Sitting Community
          </p>

          <p className={css.info}>
            Are you ready to join the HouseSit Match network? Register today to become part of a global community dedicated to making travel and pet care affordable, secure, and enjoyable.
          </p>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

export default AboutHouseSitMatchPage;
